import SearchForm, {
  normalizeSort,
} from '@/features/agent/provided_job_posts/components/SearchForm';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FormValue, SearchFormInput } from '@/features/agent/provided_job_posts/searchSchema';
import { searchSchema } from '@/features/agent/provided_job_posts/searchSchema';
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import type { SearchQuery } from '@/features/agent/provided_job_posts/hooks/useProvidedJobPosts';
import Results from '@/features/agent/provided_job_posts/components/Results';
import useProvidedJobPosts from '@/features/agent/provided_job_posts/hooks/useProvidedJobPosts';
import Loading from '@/components/Loading';
import { Button } from '@/components/Button';
import { MdAdd, MdArrowDropDown } from 'react-icons/md';
import { LuSparkles } from 'react-icons/lu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import qs from 'qs';
import compact from '@/utils/compact';
import { MainContents } from '@/components/MainContents';
import { createFileRoute, Link } from '@tanstack/react-router';
import ProvidedJobPostLoadingCard from '@/features/agent/provided_job_posts/components/ProvidedJobPostLoadingCard';
import BackToSearchBar from '@/components/BackToSearchBar';
import SelectionBar from '@/features/agent/provided_job_posts/components/SelectionBar';
import useBulkShareAndSuggest from '@/features/agent/provided_job_posts/hooks/useBulkShareAndSuggest';
import useDeleteProvidedJobPost from '@/features/agent/provided_job_posts/api/useDeleteProvidedJobPost';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import Tabs, { type TabItem } from '@/components/Tabs';
import type { AgentApiSharedJobPostsMetadata } from '@/types/serializers';
import {
  createManualSelection,
  isAllMode,
  selectionCount,
  toggleSelection,
  type Selection,
} from '@/features/agent/provided_job_posts/hooks/useSelection';
import hasActiveFilter from '@/features/agent/provided_job_posts/utils/hasActiveFilter';
import AiJobMatchingModal from '@/features/agent/provided_job_posts/components/ai_job_matching/AiJobMatchingModal';
import useFetchIam from '@/features/agent/api/useFetchIam';
import { PREFECTURES } from '@/constants/sharedJobPostConstants';

// localStorageのキー名
const SEARCH_QUERY_STORAGE_KEY = 'providedJobPostsSearchQuery';

// タブの定義
const TAB_OPTIONS = [
  { label: '自社求人', value: 'own', path: '/agent/provided_job_posts' },
  { label: '求人データベース', value: 'shared', path: '/agent/shared_job_posts' },
] as const;

type TabValue = (typeof TAB_OPTIONS)[number]['value'];

// URL クエリ / localStorage に残る旧 working_location（都道府県名の空白区切り文字列）を
// prefecture_codes（コード配列）へ変換する後方互換処理（Issue #3401）
const resolvePrefectureCodes = (q: Record<string, unknown>): number[] => {
  if (q.prefecture_codes !== undefined) {
    const raw = Array.isArray(q.prefecture_codes) ? q.prefecture_codes : [q.prefecture_codes];
    return raw.map(Number).filter((code) => !Number.isNaN(code));
  }
  if (typeof q.working_location === 'string' && q.working_location.trim() !== '') {
    const names = q.working_location.split(' ').filter((v) => !!v);
    return names
      .map((name) => PREFECTURES.find((p) => p.name === name)?.code)
      .filter((code): code is number => code !== undefined);
  }
  return [];
};

export const Route = createFileRoute('/agent/provided_job_posts/')({
  component: ProvidedJobPosts,
  staticData: { title: '求人管理' },
});

function ProvidedJobPosts() {
  const searchRef = useRef<HTMLDivElement>(null);

  // 現在のパスからタブを判定
  const getCurrentTab = (): TabValue => {
    if (typeof window === 'undefined') return 'own';
    const pathname = window.location.pathname;
    if (pathname.startsWith('/agent/shared_job_posts')) return 'shared';
    return 'own';
  };

  const currentTab = getCurrentTab();

  const form = useForm<SearchFormInput, unknown, FormValue>({
    resolver: zodResolver(searchSchema),
    defaultValues: {
      companyIds: [],
      salary: '',
      minSalary: '',
      maxSalary: '',
      jobPositions: '',

      prefectureCodes: [],
      customTagIds: [],
      customTagMatch: 'all',
      listId: undefined,
      searchColumnAlpha: 'title',
      searchKeywordAlpha: '',
      searchOperatorAlpha: 'or',
      searchColumnBeta: 'mandatory_requirement',
      searchKeywordBeta: '',
      searchOperatorBeta: 'or',
      searchColumnGamma: 'keyword_content',
      searchKeywordGamma: '',
      searchOperatorGamma: 'or',
      isIncludeArchived: false,
      isSalaryEmpty: false,
      sort: 'updated_at:desc',
      jobSourceId: undefined,
      createdType: undefined,
    },
  });
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    company_ids: [] as number[],
    job_position_name: '',
    prefecture_codes: [] as number[],
    min_salary: '',
    max_salary: '',
    salary: '',
    custom_tag_ids: [] as number[],
    list_ids: [] as number[],
    search_column_alpha: '',
    search_keyword_alpha: '',
    search_operator_alpha: '',
    search_column_beta: '',
    search_keyword_beta: '',
    search_operator_beta: '',
    search_column_gamma: '',
    search_keyword_gamma: '',
    search_operator_gamma: '',
    is_include_archived: false,
    is_salary_empty: false,
    sort: 'updated_at:desc',
    job_source_id: undefined,
    created_type: undefined,
  });

  const deleteProvidedJobPost = useDeleteProvidedJobPost();
  const queryClient = useQueryClient();

  // localStorageに検索条件を保存する関数
  const saveSearchQueryToLocalStorage = (sq: SearchQuery) => {
    try {
      const serialized = JSON.stringify(sq);
      localStorage.setItem(SEARCH_QUERY_STORAGE_KEY, serialized);
    } catch (error) {
      console.error('Failed to save search query to localStorage:', error);
    }
  };

  // localStorageから検索条件を読み込む関数
  const loadSearchQueryFromLocalStorage = (): SearchQuery | null => {
    try {
      const stored = localStorage.getItem(SEARCH_QUERY_STORAGE_KEY);
      if (!stored) return null;
      return JSON.parse(stored) as SearchQuery;
    } catch (error) {
      console.error('Failed to load search query from localStorage:', error);
      return null;
    }
  };

  const onSubmit: SubmitHandler<FormValue> = (data) => {
    const sq = {
      company_ids: data.companyIds,
      job_position_name: data.jobPositions.trim(),
      prefecture_codes: data.prefectureCodes,
      min_salary: data.isSalaryEmpty ? '' : String(data.minSalary),
      max_salary: data.isSalaryEmpty ? '' : String(data.maxSalary),
      salary: data.isSalaryEmpty ? '' : String(data.salary),
      custom_tag_ids: data.customTagIds,
      custom_tag_match: data.customTagMatch,
      list_ids: data.listId ? [Number(data.listId)] : [],
      search_column_alpha: data.searchColumnAlpha,
      search_keyword_alpha: data.searchKeywordAlpha ?? '',
      search_operator_alpha: data.searchOperatorAlpha,
      search_column_beta: data.searchColumnBeta,
      search_keyword_beta: data.searchKeywordBeta ?? '',
      search_operator_beta: data.searchOperatorBeta,
      search_column_gamma: data.searchColumnGamma,
      search_keyword_gamma: data.searchKeywordGamma ?? '',
      search_operator_gamma: data.searchOperatorGamma,
      is_include_archived: data.isIncludeArchived,
      is_salary_empty: data.isSalaryEmpty,
      sort: data.sort,
      job_source_id: data.jobSourceId,
      created_type: data.createdType,
    };

    setSearchQuery(sq);
    // 検索条件が変わったら選択状態はリセットする（全件選択が古い母集合のまま残るのを防ぐ）
    setSelection(createManualSelection());
    // localStorageに保存
    saveSearchQueryToLocalStorage(sq);
    const queryString = qs.stringify(compact(sq), { arrayFormat: 'brackets' });
    history.pushState(null, '', `?${queryString}`);
  };

  const setSearchQueryFromQueryString = () => {
    const q = qs.parse(location.search, { ignoreQueryPrefix: true });

    // クエリパラメータがある場合はそれを使用
    const hasQueryParams = Object.keys(q).length > 0;

    let sq: SearchQuery;

    if (hasQueryParams) {
      // is_salary_empty が立っている場合は salary 系条件を破棄して NULL 条件との競合を防ぐ
      const isSalaryEmpty = q.is_salary_empty === 'true' || q.is_salary_empty === '1';
      // URLクエリパラメータから復元
      sq = {
        company_ids: q.company_ids
          ? Array.isArray(q.company_ids)
            ? q.company_ids.map(Number)
            : [+q.company_ids]
          : [],
        salary: isSalaryEmpty ? '' : ((q.salary ?? '') as string),
        min_salary: isSalaryEmpty ? '' : ((q.min_salary ?? '') as string),
        max_salary: isSalaryEmpty ? '' : ((q.max_salary ?? '') as string),
        job_position_name: (q.job_position_name ?? '') as string,
        prefecture_codes: resolvePrefectureCodes(q as Record<string, unknown>),
        custom_tag_ids: q.custom_tag_ids
          ? Array.isArray(q.custom_tag_ids)
            ? q.custom_tag_ids.map(Number)
            : [+q.custom_tag_ids]
          : [],
        custom_tag_match: q.custom_tag_match === 'any' ? 'any' : 'all',
        list_ids: q.list_ids
          ? Array.isArray(q.list_ids)
            ? q.list_ids.map(Number)
            : [+q.list_ids]
          : [],
        search_column_alpha: (q.search_column_alpha ?? 'title') as string,
        search_keyword_alpha: (q.search_keyword_alpha ?? '') as string,
        search_operator_alpha: (q.search_operator_alpha ?? 'or') as string,
        search_column_beta: (q.search_column_beta ?? 'mandatory_requirement') as string,
        search_keyword_beta: (q.search_keyword_beta ?? '') as string,
        search_operator_beta: (q.search_operator_beta ?? 'or') as string,
        search_column_gamma: (q.search_column_gamma ?? 'keyword_content') as string,
        search_keyword_gamma: (q.search_keyword_gamma ?? '') as string,
        search_operator_gamma: (q.search_operator_gamma ?? 'or') as string,
        is_include_archived: q.is_include_archived === 'true',
        is_salary_empty: isSalaryEmpty,
        sort: normalizeSort(q.sort as string | undefined),
        job_source_id: q.job_source_id ? Number(q.job_source_id) : undefined,
        created_type: (q.created_type ?? undefined) as string | undefined,
      };
      // URLクエリがある場合はlocalStorageも更新
      saveSearchQueryToLocalStorage(sq);
    } else {
      // クエリパラメータがない場合はlocalStorageから復元
      const storedQuery = loadSearchQueryFromLocalStorage();
      if (storedQuery) {
        // 旧形式の working_location が残ると prefecture_codes と AND で効き「全国」求人が落ちる（Issue #3401）
        const storedCodes = resolvePrefectureCodes(
          storedQuery as unknown as Record<string, unknown>
        );
        const legacyStripped: SearchQuery & { working_location?: string } = { ...storedQuery };
        delete legacyStripped.working_location;
        sq = {
          ...legacyStripped,
          sort: normalizeSort(storedQuery.sort),
          prefecture_codes: storedCodes,
        };
        // localStorageから復元した場合はURLも更新（履歴に追加しない）
        const queryString = qs.stringify(compact(sq), { arrayFormat: 'brackets' });
        history.replaceState(null, '', `?${queryString}`);
      } else {
        // localStorageにもない場合はデフォルト値
        sq = {
          company_ids: [],
          job_position_name: '',
          prefecture_codes: [],
          min_salary: '',
          max_salary: '',
          salary: '',
          custom_tag_ids: [],
          custom_tag_match: 'all',
          list_ids: [],
          search_column_alpha: 'title',
          search_keyword_alpha: '',
          search_operator_alpha: 'or',
          search_column_beta: 'mandatory_requirement',
          search_keyword_beta: '',
          search_operator_beta: 'or',
          search_column_gamma: 'keyword_content',
          search_keyword_gamma: '',
          search_operator_gamma: 'or',
          is_include_archived: false,
          is_salary_empty: false,
          sort: 'updated_at:desc',
          job_source_id: undefined,
          created_type: undefined,
        };
      }
    }
    setSearchQuery(sq);
    // 検索条件が変わったら選択状態はリセットする（popstate 復元時も全件選択の母集合と searchQuery の不整合を防ぐ）
    setSelection(createManualSelection());

    form.setValue('companyIds', sq.company_ids ?? []);
    form.setValue('salary', sq.salary ?? '');
    form.setValue('minSalary', sq.min_salary ?? '');
    form.setValue('maxSalary', sq.max_salary ?? '');
    form.setValue('jobPositions', sq.job_position_name ?? '');
    form.setValue('prefectureCodes', sq.prefecture_codes ?? []);
    form.setValue('customTagIds', sq.custom_tag_ids ?? []);
    form.setValue('customTagMatch', sq.custom_tag_match ?? 'all');
    form.setValue('listId', sq.list_ids?.[0] ? String(sq.list_ids[0]) : undefined);
    form.setValue('searchColumnAlpha', sq.search_column_alpha ?? 'title');
    form.setValue('searchKeywordAlpha', sq.search_keyword_alpha ?? '');
    form.setValue('searchOperatorAlpha', sq.search_operator_alpha ?? 'or');
    form.setValue('searchColumnBeta', sq.search_column_beta ?? 'mandatory_requirement');
    form.setValue('searchKeywordBeta', sq.search_keyword_beta ?? '');
    form.setValue('searchOperatorBeta', sq.search_operator_beta ?? 'or');
    form.setValue('searchColumnGamma', sq.search_column_gamma ?? 'keyword_content');
    form.setValue('searchKeywordGamma', sq.search_keyword_gamma ?? '');
    form.setValue('searchOperatorGamma', sq.search_operator_gamma ?? 'or');
    form.setValue('isIncludeArchived', sq.is_include_archived ?? false);
    form.setValue('isSalaryEmpty', sq.is_salary_empty ?? false);
    form.setValue('sort', normalizeSort(sq.sort));
    form.setValue('jobSourceId', sq.job_source_id);
    form.setValue('createdType', sq.created_type);
  };
  useEffect(() => {
    setSearchQueryFromQueryString();
    window.addEventListener('popstate', setSearchQueryFromQueryString);
    return () => window.removeEventListener('popstate', setSearchQueryFromQueryString);
  }, []);

  // タブアイテムの生成
  const tabItems: TabItem[] = useMemo(() => {
    return TAB_OPTIONS.map((option) => {
      return {
        label: option.label,
        current: currentTab === option.value,
        to: option.path,
      };
    });
  }, [currentTab]);

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  // 選択状態: manual（個別チェック）または all（全件選択）
  const [selection, setSelection] = useState<Selection>(createManualSelection());

  // AI求人推薦機能の組織ゲート。許可リストはサーバー側の Ai::MatchingAvailability に集約 (Issue #3324)
  const { data: iam } = useFetchIam();
  const isAiRecommendationEnabled = iam?.is_ai_matching_enabled ?? false;

  // AI求人マッチング: 方法選択モーダルの表示状態（UIのみ）
  const [isAiMatchingOpen, setIsAiMatchingOpen] = useState(false);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useProvidedJobPosts(searchQuery);

  // permitted_providersを取得
  const { data: metadata } = useQuery<AgentApiSharedJobPostsMetadata>({
    queryKey: ['/agent/api/shared_job_posts/metadata'],
    queryFn: async () => {
      const response = await fetch('/agent/api/shared_job_posts/metadata.json');
      if (!response.ok) {
        throw new Error('Failed to fetch metadata');
      }
      return response.json();
    },
  });

  // permitted_providersが存在する場合のみタブを表示
  const shouldShowTabs = metadata?.permitted_providers && metadata.permitted_providers.length > 0;

  const providedJobPosts = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.records);
  }, [data?.pages]);

  // 検索結果の総件数
  const totalCount = data?.pages?.[0]?.total_count ?? 0;

  const {
    handleWebShare,
    handlePdfShare,
    handleSuggestToCandidates,
    handleAddToList,
    isProcessing,
    pdfProgress,
    dialogs,
  } = useBulkShareAndSuggest({
    providedJobPosts,
    selection,
    searchQuery,
  });

  const handleDelete = useCallback(
    async (id: number) => {
      await deleteProvidedJobPost.mutateAsync({ id });
      queryClient.invalidateQueries({
        queryKey: ['/agent/api/provided_job_posts'],
        exact: false,
      });
    },
    [deleteProvidedJobPost, queryClient]
  );

  const handleScroll = useCallback(() => {
    if (isFetchingNextPage || !hasNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;

    if (scrollTop + clientHeight >= scrollHeight - 500) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const debouncedScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 100);
    };

    window.addEventListener('scroll', debouncedScroll);
    return () => {
      window.removeEventListener('scroll', debouncedScroll);
      clearTimeout(timeoutId);
    };
  }, [handleScroll]);

  const handleCheckedChange = useCallback((id: number, checked: boolean) => {
    setSelection((prev) => toggleSelection(prev, id, checked));
  }, []);

  const handleDeselectAll = useCallback(() => {
    setSelection(createManualSelection());
  }, []);

  /** 検索結果全件を選択する */
  const handleSelectAll = useCallback(() => {
    setSelection({ mode: 'all', excludedIds: new Set(), totalCount });
  }, [totalCount]);

  /** 全件選択を解除して manual モードに戻す */
  const handleDeselectAllMode = useCallback(() => {
    setSelection(createManualSelection());
  }, []);

  return (
    <FormProvider {...form}>
      <MainContents>
        {/* タブ */}
        {shouldShowTabs && (
          <div className="mb-md w-[520px]">
            <Tabs items={tabItems} />
          </div>
        )}
        {/* ヘッダー部分 */}
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold">{isLoading ? '---' : totalCount}</span>
            <span className="text-sm">件</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {/* AI求人マッチングボタン（許可組織のみ表示） */}
            {isAiRecommendationEnabled && (
              <Button
                type="button"
                color="secondary"
                size="md"
                onClick={() => setIsAiMatchingOpen(true)}
              >
                <LuSparkles className="size-icon-sm" aria-hidden="true" />
                AI求人マッチング
              </Button>
            )}
            <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal>
              <PopoverTrigger asChild>
                <Button color="primary" size="md" active={isPopoverOpen}>
                  <MdAdd className="size-icon-sm" />
                  求人票を作成
                  <MdArrowDropDown
                    className={`size-icon-xs transition-transform duration-200 ${isPopoverOpen ? 'rotate-180' : ''}`}
                  />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="rounded-sm bg-white p-0">
                <div className="flex flex-col">
                  <Link
                    to="/agent/provided_job_posts/new/manual"
                    className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 no-underline hover:bg-gray-50 hover:text-gray-700"
                    onClick={() => setIsPopoverOpen(false)}
                  >
                    手動で作成
                  </Link>
                  <Link
                    to="/agent/provided_job_posts/new/csv"
                    className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 no-underline hover:bg-gray-50 hover:text-gray-700"
                    onClick={() => setIsPopoverOpen(false)}
                  >
                    CSVから作成
                  </Link>
                  <Link
                    to="/agent/provided_job_posts/new/pdf"
                    className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 no-underline hover:bg-gray-50 hover:text-gray-700"
                    onClick={() => setIsPopoverOpen(false)}
                  >
                    PDFから作成
                  </Link>
                  <Link
                    to="/agent/provided_job_posts/new/text"
                    className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 no-underline hover:bg-gray-50 hover:text-gray-700"
                    onClick={() => setIsPopoverOpen(false)}
                  >
                    テキストから作成
                  </Link>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <div ref={searchRef} className="mt-sm">
          <SearchForm
            onSubmit={onSubmit}
            clearButtonLeftSlot={
              !isLoading && totalCount > 0 && hasActiveFilter(searchQuery) ? (
                isAllMode(selection) ? (
                  <Button color="tertiary" size="md" onClick={handleDeselectAllMode}>
                    <span className="text-sm font-bold text-primary">全選択を解除</span>
                  </Button>
                ) : (
                  <Button color="tertiary" size="md" onClick={handleSelectAll}>
                    <span className="text-sm font-bold text-primary">全{totalCount}件を選択</span>
                  </Button>
                )
              ) : null
            }
          />
        </div>
        <BackToSearchBar targetRef={searchRef} />
        <div className="mt-md space-y-6 pb-8">
          {isLoading ? (
            <div className="flex flex-col gap-md">
              {Array.from({ length: 10 }).map((_, key) => {
                return <ProvidedJobPostLoadingCard key={key} />;
              })}
            </div>
          ) : (
            <>
              {providedJobPosts.length > 0 && (
                <Results
                  providedJobPosts={providedJobPosts}
                  selection={selection}
                  onCheckedChange={handleCheckedChange}
                  onDelete={handleDelete}
                />
              )}
              {(isLoading || isFetchingNextPage) && providedJobPosts.length > 0 && (
                <div className="flex justify-center py-md">
                  <Loading size="small" />
                </div>
              )}
            </>
          )}
        </div>
      </MainContents>
      <SelectionBar
        selectedCount={selectionCount(selection)}
        isProcessing={isProcessing}
        pdfProgress={pdfProgress}
        onDeselectAll={handleDeselectAll}
        onWebShare={handleWebShare}
        onPdfShare={handlePdfShare}
        onSuggestToCandidates={handleSuggestToCandidates}
        onAddToList={handleAddToList}
      />
      {dialogs}

      {/* AI求人マッチング: 方法選択モーダル（UIのみ） */}
      <AiJobMatchingModal open={isAiMatchingOpen} onOpenChange={setIsAiMatchingOpen} />
    </FormProvider>
  );
}
